import {
  Avatar,
  Box,
  IconButton,
  LinkBox,
  LinkOverlay,
  Menu,
  MenuButton,
  MenuItem,
  MenuList,
  Stack,
  Text,
  Wrap,
  WrapItem,
} from '@chakra-ui/react';
import NextLink from 'next/link';
import * as queryString from 'query-string';
import * as React from 'react';
import yn from 'yn';

import { ImagePreview } from '@/components/ImagePreview';
import { useCurrent } from '@/hooks/useCurrent';
import { usePermissions } from '@/hooks/usePermissions';
import {
  MailIcon,
  MapPinIcon,
  MessageCircleIcon,
  MoreHorizontalIcon,
  PhoneIcon,
} from '@/imports/ui/chakra/feather';
import { tokenStorage } from '@/modules/accounts/tokenStorage';
import { formatDisplayName } from '@/utilities';
import { MemberDirectory_Search_memberDirectorySearch_results } from '../../../hooks/graphql-types/MemberDirectory_Search';

export const Member: React.FC<{
  member: MemberDirectory_Search_memberDirectorySearch_results;
}> = ({ member }) => {
  const permissions = usePermissions();
  const current = useCurrent();
  const profile = member.profile;

  const getShowChatIcon = tokenStorage.getItem('showChatIcon');
  const showChatIcon = getShowChatIcon ? yn(getShowChatIcon) : true;

  const displayName = React.useMemo(() => {
    if (!profile) {
      return member.displayName || '';
    }

    return formatDisplayName({
      preferredName: profile.preferredName ?? undefined,
      prefix: profile.prefix ?? undefined,
      firstName: profile.firstName ?? undefined,
      lastName: profile.lastName ?? undefined,
      suffix: profile.suffix ?? undefined,
      nameTitle: profile.nameTitle ?? undefined,
    });
  }, [member.displayName, profile]);

  if (current.loading || !profile) {
    return null;
  }

  return (
    <LinkBox>
      <Stack direction="row" align="flex-start" spacing="4">
        <ImagePreview src={member.profilePictureUrl} alt={displayName}>
          <Avatar
            src={member.profilePictureUrl || undefined}
            name={displayName}
            boxSize={{ base: '12', md: '14' }}
          />
        </ImagePreview>
        <Stack
          direction={{ base: 'column', lg: 'row' }}
          flex="1"
          spacing="4"
          align={{ lg: 'center' }}
        >
          <Box flex="1">
            <LinkOverlay as={NextLink} href={`/members/${member._id}`}>
              <Text
                color="textPrimary.500"
                noOfLines={1}
                wordBreak="break-all"
                fontWeight="medium"
              >
                {displayName}
                {profile.nameTitle && !profile.preferredName ? (
                  <>, {profile.nameTitle}</>
                ) : null}
              </Text>
            </LinkOverlay>
            <Text color="gray.600" mt="2">
              {profile.jobTitle}
              {profile.company && profile.jobTitle ? ' at ' : ''}
              {profile.company}
            </Text>
            <Wrap color="gray.600" mt="2">
              {profile.email && (
                <WrapItem>
                  <Text wordBreak="break-word">
                    <MailIcon mr="1" boxSize={4} color="primary.500" />
                    {profile.email}
                  </Text>
                </WrapItem>
              )}
              {profile.primaryPhone && (
                <WrapItem>
                  <Text>
                    <PhoneIcon mr="1" boxSize={4} color="primary.500" />
                    {profile.primaryPhone}
                  </Text>
                </WrapItem>
              )}
              {(profile.addressLocality ||
                profile.addressAdministrativeArea ||
                profile.addressRegionCode) && (
                <WrapItem>
                  <Text>
                    <MapPinIcon mr="1" boxSize={4} color="primary.500" />
                    {profile.addressLocality}
                    {(profile.addressAdministrativeArea ||
                      profile.addressRegionCode) && (
                      <>
                        {profile.addressLocality && ', '}
                        {profile.addressAdministrativeArea}
                        {profile.addressAdministrativeArea &&
                          profile.addressRegionCode &&
                          ', '}
                        {profile.addressRegionCode}
                      </>
                    )}
                  </Text>
                </WrapItem>
              )}
              {member.cardAttributes.map((attribute) => (
                <WrapItem key={attribute.attributeSchemaId}>
                  <Text color="gray.600">
                    {attribute.name}: {attribute.displayValues.join(', ')}
                  </Text>
                </WrapItem>
              ))}
            </Wrap>
          </Box>
          <Stack direction="row">
            {current.user?._id !== member._id &&
              permissions.canAccessCommunityAndAuthenticated &&
              permissions.canAccessDmPageAndAuthenticated &&
              showChatIcon &&
              current.tenant?.directMessagesEnabled !== false && (
                <NextLink
                  href={`/messages/create?${queryString.stringify({
                    channel_member: member._id,
                  })}`}
                >
                  <IconButton
                    aria-label="Direct Message"
                    icon={<MessageCircleIcon color="primary.500" boxSize={4} />}
                    variant="outline"
                    style={{ borderColor: 'strokePrimary.500' }}
                    size="sm"
                    colorScheme="primary"
                  />
                </NextLink>
              )}
            {permissions.canDoAdminStuffAndAuthenticated && (
              <Box>
                <Menu autoSelect={false}>
                  <MenuButton
                    as={IconButton}
                    aria-label="See more"
                    icon={
                      <MoreHorizontalIcon
                        color="primary.500"
                        boxSize={4}
                        transform="rotate(90deg)"
                      />
                    }
                    variant="outline"
                    bordercolor="strokePrimary.500"
                    size="sm"
                    colorScheme="primary"
                  />
                  <MenuList>
                    <NextLink href={`/admin/membership/people/${member._id}`}>
                      <MenuItem>Edit Profile</MenuItem>
                    </NextLink>
                  </MenuList>
                </Menu>
              </Box>
            )}
          </Stack>
        </Stack>
      </Stack>
    </LinkBox>
  );
};
